/**
 * OP-Motion CAPTURE (2026-10-24, OPM4LBuilder's ask "OP-Motion PRESETS that LAND on the next bar"): a press sends
 * `/OP-Motion/capture <bus|all> <id>`, every OP-Motion on that bus answers once with `/OP-Motion/state <bus> <id>
 * <pairs...>`, OPBridge's rule (`midiTrigger/ruleSync.ts::motionStateRule`) turns each answer into a `pwa_action`, and
 * this file stores the pairs as a landing preset on the project.
 *
 * DELIVERY IS BEST EFFORT (OPBridge replays nothing on reconnect and drops frames to a lagging socket), which is what
 * the id is for: an answer is taken only for the press that asked, a late one is ignored, and a press that hears
 * nothing in a second asks once more before saying so.
 *
 * `all` cannot know how many buses will answer (an instance with no bus stays quiet), so it keeps listening until
 * nothing new has arrived for a short settle; one bus is done at its first answer.
 */

import { onPwaAction, type PwaActionEvent } from '../opbridge/consumerWs.ts';
import type { FireResult } from '../opbridge/dispatch.ts';
import { motionPresetById, nextMotionPresetName, saveMotionPreset, type MotionPreset } from '../layout/projectStore.ts';
import { pushToast } from '../ui/toast.ts';
import { ulid } from '../utils/ulid.ts';
import { MOTION_STATE_ADDRESS, MOTION_STATE_COMMAND, motionBusLabel, parseMotionState } from './opMotion.ts';

/** How long a press waits for its first answer before asking again (the ask: "retry if no reply arrives within about
 *  a second"). */
export const CAPTURE_ANSWER_MS = 1000;
/** After an answer to `all`, how long with nothing new before the capture is stored. The instances answer the same
 *  datagram, so their replies arrive together; this only has to outlast a socket's jitter. */
export const CAPTURE_SETTLE_MS = 350;

interface Pending {
  id: number;
  bus: string;
  into?: string | undefined;
  heard: Map<string, Array<string | number>>;
  timer: ReturnType<typeof setTimeout> | null;
  retried: boolean;
  send: (args: Array<string | number>) => Promise<FireResult>;
}

const pending = new Map<number, Pending>();
let lastId = (Date.now() % 100000) * 10;
const nextId = (): number => { lastId = (lastId + 1) % 2_000_000_000; return lastId; };

/** Start a capture: send it, and store what answers. Resolves with the send's own result; the preset is saved later,
 *  when the answers are in. `into` names the preset to fill (made with that id when it does not exist yet); absent,
 *  every capture is a new preset. */
export async function startMotionCapture(
  opts: { bus: string; into?: string | undefined },
  send: (args: Array<string | number>) => Promise<FireResult>,
): Promise<FireResult> {
  const p: Pending = { id: nextId(), bus: opts.bus || 'all', into: opts.into || undefined, heard: new Map(), timer: null, retried: false, send };
  pending.set(p.id, p);
  const res = await send([p.bus, p.id]);
  if (res.kind !== 'ok') { pending.delete(p.id); return res; }
  arm(p, CAPTURE_ANSWER_MS, () => void noAnswer(p));
  return res;
}

function arm(p: Pending, ms: number, fn: () => void): void {
  if (p.timer !== null) clearTimeout(p.timer);
  p.timer = setTimeout(() => { p.timer = null; fn(); }, ms);
}

async function noAnswer(p: Pending): Promise<void> {
  pending.delete(p.id);
  if (!p.retried) {
    p.retried = true;
    p.id = nextId();
    pending.set(p.id, p);
    const res = await p.send([p.bus, p.id]);
    if (res.kind === 'ok') { arm(p, CAPTURE_ANSWER_MS, () => void noAnswer(p)); return; }
    pending.delete(p.id);
  }
  pushToast('error', `OP-Motion did not answer the capture on ${motionBusLabel(p.bus)}. It needs OP-Motion v0.3 in the set on the computer running Live.`, 6000);
}

/** One `/OP-Motion/state` answer, as the rule delivers it (`args: {address, args}`). Exported for the smoke. */
export function handleMotionState(evt: PwaActionEvent): void {
  if (evt.command !== MOTION_STATE_COMMAND) return;
  const a = evt.args as { address?: unknown; args?: unknown } | null;
  if (!a || typeof a !== 'object' || (a.address !== undefined && a.address !== MOTION_STATE_ADDRESS)) return;
  const st = parseMotionState(a.args);
  if (!st) return;
  const p = pending.get(st.id);
  // Late (the press already stored or gave up), or another app's capture: ignored.
  if (!p) return;
  if (p.bus !== 'all' && st.bus !== p.bus) return;
  if (st.pairs.length === 0) return;
  p.heard.set(st.bus, st.pairs);
  if (p.bus === 'all') arm(p, CAPTURE_SETTLE_MS, () => finish(p));
  else finish(p);
}

function finish(p: Pending): void {
  if (p.timer !== null) { clearTimeout(p.timer); p.timer = null; }
  pending.delete(p.id);
  const existing = motionPresetById(p.into);
  const heard = Object.fromEntries(p.heard);
  // `all` is the whole picture: it replaces. One bus is a part of it: it replaces that bus only.
  const buses = p.bus === 'all' || !existing ? heard : { ...existing.buses, ...heard };
  const preset: MotionPreset = {
    id: existing?.id ?? p.into ?? `motion-${ulid()}`,
    name: existing?.name ?? nextMotionPresetName(),
    buses,
    capturedAt: Date.now(),
  };
  saveMotionPreset(preset);
  const which = Object.keys(heard).map(motionBusLabel).join(', ');
  pushToast('ok', `Captured ${preset.name} (${which})`, 2500);
}

let started = false;

/** Listen for capture answers. Idempotent; called once at boot. */
export function startMotionCaptureListener(): void {
  if (started) return;
  started = true;
  onPwaAction(handleMotionState);
}
